import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { openClose } from '@core/animations/animations';
import { addressString } from '@core/util/address';
import { Address } from '@models/address';
import { FireInsuranceType, MatterFieldMappings } from '@models/matter';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { MatterDispatch } from '@state/matter';
import { Select, Store } from '@ngxs/store';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { AI_GENERATED_ICON, ICON } from '@core/util/icon';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';
import { DropdownResult } from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { map, take } from 'rxjs/operators';
import { YES_NO_OPTIONS } from 'src/app/ui/utils/constants';
import { TestHooks } from './fire-insurance-side-form.hooks';
import {
	FireInsuranceBroker,
	FireInsuranceBrokerage,
	FireInsuranceCompany,
	FireInsuranceAgent,
} from '@models/brokerage';
import { Employee } from '@models/company';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MatterAiJobServiceRepo } from '@repos/matter-ai-job/matter-ai-job.service';
import { Observable, Subscription } from 'rxjs';
import { DocumentProcessorService } from '@services/document-ai/document-processor.service';
import { MatterDocumentType } from '@models/matter-file';

@UntilDestroy()
@Component({
	selector: 'app-fire-insurance-side-form',
	templateUrl: './fire-insurance-side-form.component.html',
	animations: [openClose(300)],
	standalone: false,
})
export class FireInsuranceSideFormComponent implements OnInit {
	testHooks = TestHooks;
	matterDocumentType = MatterDocumentType.FIRE_INSURANCE;
	public type;
	public policyNumber;
	public guaranteedReplacementCoverage;
	public expiryDate;
	public activeDate;
	public coverageAmount;
	public interestOfVTBNotes;

	@SelectSnapshot(SelectedMatter.id) matterId: string;

	@ViewSelectSnapshot(SelectedMatter.fireInsuranceCompany) fireInsuranceCompany;
	@ViewSelectSnapshot(SelectedMatter.fireInsuranceBrokerage)
	fireInsuranceBrokerage;
	@ViewSelectSnapshot(SelectedMatter.fireInsuranceBroker) fireInsuranceBroker;
	@ViewSelectSnapshot(SelectedMatter.fireInsuranceAgent) fireInsuranceAgent;

	public fireInsurance$ = this.store.select(SelectedMatter.fireInsurance);

	public showFireInsuranceCompanyForm = false;
	public fireInsuranceCompanySearchValue = '';

	public showFireInsuranceBrokerageForm = false;
	public fireInsuranceBrokerageSearchValue = '';

	public showFireInsuranceBrokerForm = false;
	public fireInsuranceBrokerSearchValue = '';

	public showFireInsuranceAgentForm = false;
	public fireInsuranceAgentSearchValue = '';

	public ICON = ICON;
	public yesNoOptions = YES_NO_OPTIONS;
	public policyTypes = [
		{ label: 'Policy number', value: FireInsuranceType.POLICY },
		{ label: 'Binder number', value: FireInsuranceType.BINDER },
	];

	private aiJobSubscription: Subscription;
	private activeAiJobId: string;

	public AI_ICON = AI_GENERATED_ICON.icon;
	public AI_TOOLTIP = AI_GENERATED_ICON.tooltip;
	public AI_CLASSES = AI_GENERATED_ICON.classes;

	public fieldMappings$ = this.store
		.select(SelectedMatter.getFieldMappings)
		.pipe(map((mappings) => mappings));

	@Select(SelectedMatter.getFieldMappings)
	sale$: Observable<MatterFieldMappings>;

	public loading$ = this.documentProcessorService.isLoading$(
		MatterDocumentType.FIRE_INSURANCE,
	);

	constructor(
		private matterDispatch: MatterDispatch,
		private navigationService: NavigationService,
		private route: ActivatedRoute,
		private store: Store,
		private matterAiJobService: MatterAiJobServiceRepo,
		private documentProcessorService: DocumentProcessorService,
	) {}

	async ngOnInit(): Promise<void> {
		this.sale$.pipe(untilDestroyed(this)).subscribe((value) => {
			console.log('FIRE INSURANCE MAPPINGS', value);
		});

		this.fireInsurance$
			.pipe(untilDestroyed(this))
			.subscribe((fireInsurance) => {
				if (fireInsurance?.aiJobId !== this.activeAiJobId) {
					this.activeAiJobId = fireInsurance.aiJobId;

					this.aiJobSubscription?.unsubscribe();

					this.aiJobSubscription = this.matterAiJobService
						.Get$(fireInsurance.aiJobId, { matterId: this.matterId })
						.pipe(untilDestroyed(this))
						.subscribe((job) => {
							console.log('JOB DATA', job);

							if (job?.status === 'failed') return;

							if (job?.data && job.status !== 'mapped') {
								console.log('Setting data', job.data);
								this.matterAiJobService.Update(
									fireInsurance.aiJobId,
									{
										isActive: false,
										status: 'mapped',
									},
									{ matterId: this.matterId },
								);
							}
						});
				}

				this.type = fireInsurance?.type;
				this.policyNumber = fireInsurance?.policyNumber;
				this.guaranteedReplacementCoverage =
					fireInsurance?.guaranteedReplacementCoverage;
				this.expiryDate = fireInsurance?.expiryDate;
				this.activeDate = fireInsurance?.activeDate;
				this.coverageAmount = fireInsurance?.coverageAmount;
				this.interestOfVTBNotes = fireInsurance?.interestOfVTBNoted;
			});
	}

	toggleFireInsuranceCompanyForm(searchValue = null) {
		this.fireInsuranceCompanySearchValue = searchValue;
		this.showFireInsuranceCompanyForm = !this.showFireInsuranceCompanyForm;
	}

	toggleFireInsuranceBrokerageForm(searchValue = null) {
		this.fireInsuranceBrokerageSearchValue = searchValue;
		this.showFireInsuranceBrokerageForm = !this.showFireInsuranceBrokerageForm;
	}

	toggleFireInsuranceBrokerForm(searchValue = null) {
		this.fireInsuranceBrokerSearchValue = searchValue;
		this.showFireInsuranceBrokerForm = !this.showFireInsuranceBrokerForm;
	}

	toggleFireInsuranceAgentForm(searchValue = null) {
		this.fireInsuranceAgentSearchValue = searchValue;
		this.showFireInsuranceAgentForm = !this.showFireInsuranceAgentForm;
	}

	setAddress(address: Address): string {
		return addressString(address);
	}

	fireInsuranceCompanyDropdownOptionSelect(event) {
		switch (event.result.name) {
			case 'Edit':
				this.navigationService.goToUrl('edit-fire-insurance-company', null, {
					relativeTo: this.route,
					state: { fireInsuranceCompany: this.fireInsuranceCompany },
				});
				break;
			case 'Remove':
				this.matterDispatch.RemoveFireInsuranceCompany(this.matterId);
				break;
		}
	}

	fireInsuranceBrokerageDropdownOptionSelect(event) {
		switch (event.result.name) {
			case 'Edit':
				this.navigationService.goToUrl('edit-fire-insurance-brokerage', null, {
					relativeTo: this.route,
					state: { fireInsuranceBrokerage: this.fireInsuranceBrokerage },
				});
				break;
			case 'Remove':
				this.matterDispatch.RemoveFireInsuranceBrokerage(this.matterId);
				break;
		}
	}

	setType(value) {
		this.matterDispatch.SetFireInsuranceType(this.matterId, value);
	}

	setPolicyNumber(value) {
		this.matterDispatch.SetFireInsurancePolicyNumber(this.matterId, value);
	}

	setGuaranteedReplacementCoverage(value) {
		this.matterDispatch.SetFireInsuranceGuaranteedReplacementCoverage(
			this.matterId,
			value,
		);
	}

	setExpiryDate(value) {
		this.matterDispatch.SetFireInsuranceExpiryDate(this.matterId, value);
	}

	setActiveDate(value) {
		this.matterDispatch.SetFireInsuranceActiveDate(this.matterId, value);
	}

	setCoverageAmount(value) {
		this.matterDispatch.SetFireInsuranceCoverageAmount(this.matterId, value);
	}

	setInterestOfVTBNotes(value) {
		this.matterDispatch.SetFireInsuranceInterestOfVTBNoted(
			this.matterId,
			value,
		);
	}

	onSelectFireInsuranceBrokerage(
		fireInsuranceBrokerage: DropdownResult<FireInsuranceBrokerage>,
	) {
		this.matterDispatch.SetFireInsuranceBrokerage(
			this.matterId,
			fireInsuranceBrokerage.result.id,
		);
	}

	onSelectFireInsuranceCompany(
		fireInsuranceCompany: DropdownResult<FireInsuranceCompany>,
	) {
		this.matterDispatch.SetFireInsuranceCompany(
			this.matterId,
			fireInsuranceCompany.result.id,
		);
	}

	onSelectFireInsuranceAgent(fireInsuranceAgent: FireInsuranceAgent) {
		this.matterDispatch.SetFireInsuranceAgent(
			this.matterId,
			this.fireInsuranceCompany.id,
			fireInsuranceAgent.id,
		);
	}

	onSubmitFireInsuranceAgent($event: Employee) {
		this.matterDispatch.CreateFireInsuranceAgent(
			this.matterId,
			this.fireInsuranceCompany.id,
			{
				...$event,
				companyId: this.fireInsuranceCompany.id,
			} as FireInsuranceAgent,
		);
		this.showFireInsuranceAgentForm = false;
	}

	onSelectFireInsuranceBroker(fireInsuranceBroker: FireInsuranceBroker) {
		this.matterDispatch.SetFireInsuranceBroker(
			this.matterId,
			this.fireInsuranceBrokerage.id,
			fireInsuranceBroker.id,
		);
	}

	onSubmitFireInsuranceBroker($event: Employee) {
		this.matterDispatch.CreateFireInsuranceBroker(
			this.matterId,
			this.fireInsuranceBrokerage.id,
			{
				...$event,
				brokerageId: this.fireInsuranceBrokerage.id,
			} as FireInsuranceBroker,
		);
		this.showFireInsuranceBrokerForm = false;
	}

	fireInsuranceBrokerDropdownOptionSelect(event) {
		switch (event.result.name) {
			case 'Edit':
				this.navigationService.goToUrl('edit-fire-insurance-broker', null, {
					relativeTo: this.route,
					state: { fireInsuranceBroker: this.fireInsuranceBroker },
				});
				break;
			case 'Remove':
				this.matterDispatch.RemoveFireInsuranceBroker(this.matterId);
				break;
		}
	}

	fireInsuranceAgentDropdownOptionSelect(event) {
		switch (event.result.name) {
			case 'Edit':
				this.navigationService.goToUrl('edit-fire-insurance-agent', null, {
					relativeTo: this.route,
					state: { fireInsuranceAgent: this.fireInsuranceAgent },
				});
				break;
			case 'Remove':
				this.matterDispatch.RemoveFireInsuranceAgent(this.matterId);
				break;
		}
	}
}
